/**
 * 食事シートの下の帯
 *
 * 選んだ品数・合計 kcal・記録後の残り kcal と記録ボタンを、キーボードの上に固定して出す。
 * 帯をタップすると選んだ食品の一覧が広がり、倍率の変更と削除ができる。
 */

import { ChevronDown, ChevronUp, Loader2, ShoppingCart, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import type { UnifiedCartItem } from './cart-utils'
import { UnifiedCart } from './unified-cart'

type Props = {
  items: UnifiedCartItem[]
  onMultiplierChange: (item: UnifiedCartItem, multiplier: number) => void
  onRemove: (item: UnifiedCartItem) => void
  /** 目標 − 今日の合計。目標が無いときは null（残りを出さない） */
  remainingCalories: number | null
  label: string
  onSubmit: () => void
  canSubmit: boolean
  submitting: boolean
  /** 編集のときだけ食事の削除ボタンを出す */
  onDelete?: () => void
}

export function CartTray({
  items,
  onMultiplierChange,
  onRemove,
  remainingCalories,
  label,
  onSubmit,
  canSubmit,
  submitting,
  onDelete,
}: Props) {
  const [expanded, setExpanded] = useState(false)
  const cartCalories = Math.round(items.reduce((s, i) => s + i.calories, 0))
  const remainingAfter = remainingCalories === null ? null : Math.round(remainingCalories - cartCalories)
  const isOpen = expanded && items.length > 0

  return (
    <div className="border-t border-border bg-background px-4 pt-2 pb-6">
      {isOpen && (
        <div className="mb-2 max-h-[40vh] overflow-y-auto">
          <UnifiedCart items={items} onMultiplierChange={onMultiplierChange} onRemove={onRemove} />
        </div>
      )}
      <button
        type="button"
        className="flex w-full items-center gap-1.5 px-0! py-1! text-xs text-muted-foreground disabled:cursor-default"
        onClick={() => setExpanded((v) => !v)}
        disabled={items.length === 0}
        aria-expanded={isOpen}
        aria-label="選んだ食品"
      >
        {items.length > 0 && (isOpen ? <ChevronDown className="size-3.5" /> : <ChevronUp className="size-3.5" />)}
        <ShoppingCart className="size-3.5" />
        {items.length > 0 ? (
          <span className="tabular-nums text-foreground">
            {items.length}品 {cartCalories}kcal
          </span>
        ) : (
          <span>まだ選んでいません</span>
        )}
        {remainingAfter !== null && (
          <span className={cn('ml-auto tabular-nums', remainingAfter < 0 && 'font-medium text-destructive')}>
            {remainingAfter >= 0 ? `記録後の残り ${remainingAfter}kcal` : `${-remainingAfter}kcal オーバー`}
          </span>
        )}
      </button>
      <div className="mt-2 flex gap-2">
        {onDelete && (
          <Button
            variant="destructive"
            size="icon"
            onClick={onDelete}
            disabled={submitting}
            aria-label="この食事を削除"
            title="この食事を削除"
          >
            <Trash2 className="size-4" />
          </Button>
        )}
        <Button className="flex-1" onClick={onSubmit} disabled={submitting || !canSubmit}>
          {submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
          {label}
        </Button>
      </div>
    </div>
  )
}
